/**
 * @file responsiveController
 * レスポンシブ制御ユーティリティ（メディアクエリ監視）。
 *
 * ブレイクポイント設定に基づき現在のデバイス種別を判定し、
 * デバイス種別の変化をトリガーに登録済みコールバックを実行する。
 */

/**
 * @typedef {Object} BreakpointSetting
 * @property {string} device デバイス識別子（例: 'sp', 'pad', 'pc'）
 * @property {number} width min-width(px)
 */

/**
 * @typedef {Object} ResponsiveEventObject
 * @property {Function} fn ブレイクポイント変化時に呼ばれる関数
 * @property {Object} [parent=window] fn実行時のthisコンテキスト
 * @property {Object} [params={}] fnに渡す追加パラメータ
 * @property {boolean|number} [first] 登録時に初回実行する場合は true。遅延実行する場合は ms 数値
 */

/**
 * @typedef {Function|ResponsiveEventObject} ResponsiveEvent
 */

const DEFAULT_BREAKPOINTS = [
    { device: 'sp', width: 0 },
    { device: 'pad', width: 768 },
    { device: 'pc', width: 980 },
];

/**
 * ブレイクポイント設定を正規化する
 * @param {BreakpointSetting[]|undefined} settings
 * @returns {BreakpointSetting[]}
 */
function normalizeBreakpoints(settings) {
    return Array.isArray(settings) && settings.length && settings[0]?.device
        ? settings
        : DEFAULT_BREAKPOINTS;
}

/**
 * Responsive controller factory
 * @param {BreakpointSetting[]} [breakpointSettings]
 * @returns {{
 *   setEvents: (event: ResponsiveEvent) => void,
 *   getCurrentDevice: () => string
 * }}
 */
export const responsiveController = ((breakpointSettings) => {
    const breakpoints = normalizeBreakpoints(breakpointSettings);
    const mql = new Map();
    const events = [];

    /**
     * 現在のデバイス種別を取得する
     * @returns {string}
     */
    const getCurrentDevice = () => {
        let current = breakpoints[0]?.device ?? 'sp';

        breakpoints.forEach(({ device }) => {
            const mediaQuery = mql.get(device);
            if (mediaQuery?.matches) {
                current = device;
            }
        });

        return current;
    };

    /**
     * メディアクエリ変化時のイベントハンドラ
     * @param {MediaQueryListEvent} _event
     */
    const checkBreakPoint = (_event) => {
        const device = getCurrentDevice();
        events.forEach(({ fn, parent, params }) => fn.call(parent, device, params));
    };

    /**
     * ブレイクポイント定義に対して監視をセットする
     */
    breakpoints.forEach(({ device, width }) => {
        if (!Number.isFinite(width) || width <= 0) return;
        if (typeof window === 'undefined' || !window.matchMedia) return;

        const mediaQuery = window.matchMedia(`screen and (min-width: ${width}px)`);
        mql.set(device, mediaQuery);

        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', checkBreakPoint);
        } else if (typeof mediaQuery.addListener === 'function') {
            mediaQuery.addListener(checkBreakPoint);
        }
    });

    /**
     * イベントを登録する
     * @param {ResponsiveEvent} event
     * @returns {void}
     */
    const setEvents = (event) => {
        if (typeof event === 'function') {
            events.push({ parent: window, fn: event, params: {} });
            return;
        }

        if (!event || typeof event.fn !== 'function') return;

        const parent = event.parent ?? window;
        const params = event.params ?? {};

        events.push({ parent, fn: event.fn, params });

        if (event.first) {
            const device = getCurrentDevice();
            const delay = Number(event.first);

            if (Number.isFinite(delay)) {
                setTimeout(() => event.fn.call(parent, device, params), delay);
            } else {
                event.fn.call(parent, device, params);
            }
        }
    };

    return {
        setEvents,
        getCurrentDevice,
    };
})();
